// Skins #18-#27: Meme Coin character builders
// Dogecoin, Satoshi, Memelord, BONK, Dogwifhat, Floki, MOG, BRETT, POPCAT, SPX6900

import * as THREE from 'three';
import {
  SEG, darken, lighten, mat, matEmissive, matSkin, enableShadows, buildHumanoid,
  addBuzzCut, addShortHair,
} from './skin-utils';

/* ------------------------------------------------------------------ */
/*  #18 — Dogecoin: Gold doge warrior with coin emblem & Comic Sans   */
/*        energy. "The People's Crypto" — gold tactical with Shiba    */
/*        motifs, distinct from The Shiba (which is a dog suit).      */
/* ------------------------------------------------------------------ */

export function buildDogecoin(color: number): THREE.Group {
  const group = new THREE.Group();
  const skinColor = 0xe0c8a0;
  const vestColor = darken(color, 0.7);

  const body = buildHumanoid({
    bodyColor: vestColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.2,
    armColor: vestColor,
    legColor: darken(vestColor, 0.8),
    bootColor: darken(color, 0.4),
    handColor: color, // gold gloves
    eyeColor: 0x664422,
  });
  group.add(body);

  // Gold tactical helmet with Doge ear silhouettes
  const helmet = new THREE.Mesh(
    new THREE.SphereGeometry(0.17, SEG, SEG, 0, Math.PI * 2, 0, Math.PI * 0.6),
    mat(color, { roughness: 0.3, metalness: 0.7 }),
  );
  helmet.position.set(0, 1.76, 0);
  group.add(helmet);

  // Doge ear shapes on helmet
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(
      new THREE.ConeGeometry(0.04, 0.10, 6),
      mat(lighten(color, 1.2), { roughness: 0.3, metalness: 0.6 }),
    );
    ear.position.set(side * 0.12, 1.90, 0.02);
    ear.rotation.z = side * 0.2;
    group.add(ear);
  }

  // Large gold coin emblem on chest — the iconic "D" coin
  const coin = new THREE.Mesh(
    new THREE.CylinderGeometry(0.06, 0.06, 0.01, 16),
    mat(color, { roughness: 0.15, metalness: 0.85 }),
  );
  coin.position.set(0, 1.22, -0.20);
  coin.rotation.x = Math.PI / 2;
  group.add(coin);
  // Inner coin ring
  const coinRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.05, 0.006, 8, 16),
    mat(lighten(color, 1.3), { roughness: 0.2, metalness: 0.8 }),
  );
  coinRing.position.set(0, 1.22, -0.21);
  coinRing.rotation.x = Math.PI / 2;
  group.add(coinRing);
  // "D" letter on coin (vertical bar + curve)
  const dBar = new THREE.Mesh(
    new THREE.BoxGeometry(0.008, 0.06, 0.008),
    mat(darken(color, 0.5)),
  );
  dBar.position.set(-0.012, 1.22, -0.215);
  group.add(dBar);
  const dCurve = new THREE.Mesh(
    new THREE.TorusGeometry(0.025, 0.005, 6, 8, Math.PI),
    mat(darken(color, 0.5)),
  );
  dCurve.position.set(-0.012, 1.22, -0.215);
  dCurve.rotation.y = Math.PI / 2;
  dCurve.rotation.x = Math.PI / 2;
  group.add(dCurve);

  // Gold shoulder pads
  for (const side of [-1, 1]) {
    const pad = new THREE.Mesh(
      new THREE.SphereGeometry(0.06, 8, 8),
      mat(color, { roughness: 0.25, metalness: 0.7 }),
    );
    pad.position.set(side * 0.30, 1.42, 0);
    pad.scale.set(1.2, 0.5, 0.9);
    group.add(pad);
  }

  // Gold belt with coin buckle
  const belt = new THREE.Mesh(
    new THREE.BoxGeometry(0.36, 0.04, 0.26),
    mat(color, { roughness: 0.3, metalness: 0.6 }),
  );
  belt.position.set(0, 0.96, 0);
  group.add(belt);

  // Small gold visor on helmet
  const visor = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.03, 0.04),
    mat(darken(color, 0.6), { roughness: 0.2, metalness: 0.8 }),
  );
  visor.position.set(0, 1.72, -0.15);
  visor.rotation.x = 0.2;
  group.add(visor);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #19 — Satoshi: Anonymous cypherpunk, hooded with orange BTC       */
/*        accents, blockchain hash text, laser eyes toggle effect.    */
/* ------------------------------------------------------------------ */

export function buildSatoshi(color: number): THREE.Group {
  const group = new THREE.Group();
  const robeColor = 0x0a0a0a;
  const btcOrange = color; // 0xF7931A

  const body = buildHumanoid({
    bodyColor: robeColor,
    headColor: 0x1a1a1a, // face hidden in shadow
    armColor: robeColor,
    legColor: robeColor,
    bootColor: 0x0a0a0a,
    handColor: 0x0a0a0a,
    showFace: false,
  });
  group.add(body);

  // Deep hood — mysterious
  const hood = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.30, SEG),
    mat(0x111111),
  );
  hood.position.set(0, 1.86, 0.02);
  group.add(hood);
  // Hood brim
  const hoodBrim = new THREE.Mesh(
    new THREE.TorusGeometry(0.16, 0.012, 6, 12, Math.PI),
    mat(0x222222),
  );
  hoodBrim.position.set(0, 1.72, -0.08);
  hoodBrim.rotation.x = 0.2;
  group.add(hoodBrim);

  // Face is obscured — just two orange laser eye glows visible
  for (const side of [-0.04, 0.04]) {
    const eyeGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.018, 8, 8),
      matEmissive(btcOrange, 3),
    );
    eyeGlow.position.set(side, 1.68, -0.13);
    group.add(eyeGlow);
    // Laser trail from eyes
    const trail = new THREE.Mesh(
      new THREE.CylinderGeometry(0.003, 0.008, 0.06, 4),
      matEmissive(btcOrange, 1.5),
    );
    trail.position.set(side, 1.68, -0.17);
    trail.rotation.x = Math.PI / 2;
    group.add(trail);
  }

  // Bitcoin "B" emblem on chest — orange on black
  const bCoin = new THREE.Mesh(
    new THREE.CylinderGeometry(0.045, 0.045, 0.008, 16),
    mat(btcOrange, { roughness: 0.3, metalness: 0.6 }),
  );
  bCoin.position.set(0, 1.22, -0.20);
  bCoin.rotation.x = Math.PI / 2;
  group.add(bCoin);
  // B vertical bars
  for (const dx of [-0.008, 0.008]) {
    const bar = new THREE.Mesh(
      new THREE.BoxGeometry(0.005, 0.05, 0.005),
      mat(0x0a0a0a),
    );
    bar.position.set(dx, 1.22, -0.205);
    group.add(bar);
  }

  // Orange circuit-line accents running down the robe
  for (let i = 0; i < 6; i++) {
    const rng = Math.sin(i * 47.3) * 0.5 + 0.5;
    const line = new THREE.Mesh(
      new THREE.BoxGeometry(0.004, 0.08 + rng * 0.06, 0.004),
      matEmissive(btcOrange, 0.6),
    );
    line.position.set(
      -0.08 + (i % 3) * 0.08,
      0.80 + Math.floor(i / 3) * 0.20,
      -0.18,
    );
    group.add(line);
  }

  // Blockchain hash text strips on arms (small orange rectangles)
  for (const side of [-1, 1]) {
    for (let j = 0; j < 4; j++) {
      const hash = new THREE.Mesh(
        new THREE.BoxGeometry(0.025, 0.006, 0.006),
        matEmissive(btcOrange, 0.4),
      );
      hash.position.set(side * 0.30, 1.10 + j * 0.08, -0.04);
      group.add(hash);
    }
  }

  // Dark gloves with orange fingertip glow
  for (const side of [-1, 1]) {
    const glowTip = new THREE.Mesh(
      new THREE.SphereGeometry(0.015, 6, 6),
      matEmissive(btcOrange, 1.5),
    );
    glowTip.position.set(side * 0.28, 0.76, 0);
    group.add(glowTip);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #20 — Memelord: Cybertruck angular armor, tech mogul aesthetic,   */
/*        stainless steel brushed finish, SpaceX/X patches, Doge tag */
/* ------------------------------------------------------------------ */

export function buildMemelord(color: number): THREE.Group {
  const group = new THREE.Group();
  const steelColor = color; // 0xC0C0C0
  const skinColor = 0xe0c8a0;
  const accentColor = 0x1DA1F2; // X/Twitter blue

  const body = buildHumanoid({
    bodyColor: steelColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.3,
    armColor: steelColor,
    legColor: darken(steelColor, 0.7),
    bootColor: 0x222222,
    handColor: steelColor,
    shoulderWidth: 0.32,
    muscular: true,
    eyeColor: 0x4488cc,
  });
  group.add(body);

  // Angular Cybertruck-style chest armor plate (sharp edges)
  const chestPlate = new THREE.Mesh(
    new THREE.BoxGeometry(0.32, 0.22, 0.06),
    mat(steelColor, { roughness: 0.15, metalness: 0.9 }),
  );
  chestPlate.position.set(0, 1.24, -0.16);
  chestPlate.rotation.x = 0.08;
  group.add(chestPlate);

  // Angular shoulder armor (triangular)
  for (const side of [-1, 1]) {
    const shoulderArmor = new THREE.Mesh(
      new THREE.ConeGeometry(0.06, 0.08, 4),
      mat(steelColor, { roughness: 0.15, metalness: 0.9 }),
    );
    shoulderArmor.position.set(side * 0.32, 1.44, 0);
    shoulderArmor.rotation.z = side * 0.3;
    group.add(shoulderArmor);
  }

  // Buzzcut hair
  addBuzzCut(group, 0x3a3a3a, 1.66, 0.14);

  // Smug grin
  const grin = new THREE.Mesh(
    new THREE.TorusGeometry(0.03, 0.005, 4, 8, Math.PI),
    mat(0xcc8888),
  );
  grin.position.set(0, 1.54, -0.125);
  grin.rotation.x = Math.PI;
  group.add(grin);

  // X logo patch on left shoulder (small circle)
  const xPatch = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.005, 8),
    mat(accentColor, { roughness: 0.3, metalness: 0.5 }),
  );
  xPatch.position.set(-0.32, 1.36, -0.04);
  xPatch.rotation.x = Math.PI / 2;
  xPatch.rotation.z = 0.5;
  group.add(xPatch);

  // Doge dog tag necklace
  const tagChain = new THREE.Mesh(
    new THREE.TorusGeometry(0.10, 0.004, 6, 12),
    mat(0xaaaaaa, { roughness: 0.2, metalness: 0.8 }),
  );
  tagChain.position.set(0, 1.42, -0.04);
  tagChain.rotation.x = Math.PI / 2 + 0.2;
  group.add(tagChain);
  // Dog tag pendant
  const tag = new THREE.Mesh(
    new THREE.BoxGeometry(0.025, 0.035, 0.003),
    mat(0xd4a017, { roughness: 0.3, metalness: 0.7 }),
  );
  tag.position.set(0, 1.34, -0.14);
  group.add(tag);

  // Bulletproof glass crack pattern on chest (decorative lines)
  for (let i = 0; i < 3; i++) {
    const crack = new THREE.Mesh(
      new THREE.BoxGeometry(0.003, 0.10 + i * 0.02, 0.003),
      mat(0xdddddd),
    );
    crack.position.set(-0.04 + i * 0.04, 1.24, -0.195);
    crack.rotation.z = (i - 1) * 0.3;
    group.add(crack);
  }

  // Rocket flame cuff accents on boots
  for (const side of [-1, 1]) {
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.03, 0.06, 6),
      matEmissive(0xff6600, 1.5),
    );
    flame.position.set(side * 0.09, 0.06, 0.08);
    flame.rotation.x = Math.PI;
    group.add(flame);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #21 — BONK: Orange Solana dog with aviator sunglasses, bomber     */
/*        jacket, baseball bat energy. "Community Airdrop" dog.       */
/* ------------------------------------------------------------------ */

export function buildBonk(color: number): THREE.Group {
  const group = new THREE.Group();
  const jacketColor = darken(color, 0.7); // dark orange bomber
  const skinColor = 0xe0c0a0;
  const solanaPurple = 0x9945FF;

  const body = buildHumanoid({
    bodyColor: jacketColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.15,
    armColor: jacketColor,
    legColor: solanaPurple,
    bootColor: 0x222222,
    handColor: color,
    showFace: false, // dog helmet
    eyeColor: 0x553311,
  });
  group.add(body);

  // BONK dog head (round, grinning Shiba)
  const dogHead = new THREE.Mesh(new THREE.SphereGeometry(0.19, SEG, SEG), mat(color));
  dogHead.position.set(0, 1.72, 0);
  dogHead.scale.set(1.05, 1.0, 0.95);
  group.add(dogHead);

  // White muzzle
  const muzzle = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 8, 8),
    mat(0xf5f0e0),
  );
  muzzle.position.set(0, 1.66, -0.14);
  muzzle.scale.set(1.0, 0.7, 0.6);
  group.add(muzzle);

  // Wide grinning mouth
  const grin = new THREE.Mesh(
    new THREE.TorusGeometry(0.06, 0.008, 6, 10, Math.PI),
    mat(0x222222),
  );
  grin.position.set(0, 1.62, -0.15);
  grin.rotation.x = Math.PI;
  group.add(grin);

  // Black nose
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 8), mat(0x0a0a0a));
  nose.position.set(0, 1.68, -0.20);
  group.add(nose);

  // Dog ears (floppy)
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(
      new THREE.SphereGeometry(0.055, 8, 8),
      mat(darken(color, 0.8)),
    );
    ear.position.set(side * 0.14, 1.86, 0.02);
    ear.scale.set(0.5, 0.9, 0.4);
    group.add(ear);
  }

  // Aviator sunglasses — signature BONK accessory
  const sungBridge = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.008, 0.01),
    mat(0x222222),
  );
  sungBridge.position.set(0, 1.74, -0.17);
  group.add(sungBridge);
  for (const side of [-1, 1]) {
    // Frame
    const frame = new THREE.Mesh(
      new THREE.TorusGeometry(0.035, 0.004, 6, 10),
      mat(0xccaa44, { roughness: 0.2, metalness: 0.8 }),
    );
    frame.position.set(side * 0.06, 1.74, -0.17);
    frame.rotation.x = Math.PI / 2;
    group.add(frame);
    // Lens
    const lens = new THREE.Mesh(
      new THREE.CircleGeometry(0.032, 10),
      mat(0x1a1a1a, { roughness: 0.1, metalness: 0.4 }),
    );
    lens.position.set(side * 0.06, 1.74, -0.175);
    group.add(lens);
  }

  // Bomber jacket collar
  const collar = new THREE.Mesh(
    new THREE.TorusGeometry(0.14, 0.025, 6, 12, Math.PI),
    mat(darken(jacketColor, 0.8)),
  );
  collar.position.set(0, 1.46, -0.06);
  collar.rotation.x = -0.3;
  group.add(collar);

  // BONK patch on chest (small orange circle with star)
  const patch = new THREE.Mesh(
    new THREE.CylinderGeometry(0.035, 0.035, 0.005, 10),
    mat(color, { roughness: 0.3, metalness: 0.5 }),
  );
  patch.position.set(0.08, 1.28, -0.20);
  patch.rotation.x = Math.PI / 2;
  group.add(patch);

  // Solana purple accent stripe on pants
  for (const side of [-1, 1]) {
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.01, 0.30, 0.01),
      matEmissive(solanaPurple, 0.5),
    );
    stripe.position.set(side * 0.06, 0.55, -0.04);
    group.add(stripe);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #22 — Dogwifhat: The iconic pink beanie. Cozy tactical gear,     */
/*        every headpiece replaced with that pink knitted beanie.     */
/* ------------------------------------------------------------------ */

export function buildDogwifhat(color: number): THREE.Group {
  const group = new THREE.Group();
  const beanieColor = color; // 0xE8A0B0 pink
  const skinColor = 0xd4b896;
  const suitColor = 0x8b7355; // warm brown tactical

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.1,
    armColor: suitColor,
    legColor: darken(suitColor, 0.8),
    bootColor: 0x5a4a3a,
    handColor: skinColor,
    showFace: false, // Dog face
    eyeColor: 0x553311,
  });
  group.add(body);

  // Shiba dog head
  const dogHead = new THREE.Mesh(new THREE.SphereGeometry(0.18, SEG, SEG), mat(0xd4943a));
  dogHead.position.set(0, 1.72, 0);
  dogHead.scale.set(1.05, 1.0, 0.95);
  group.add(dogHead);

  // White cheeks
  for (const side of [-1, 1]) {
    const cheek = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8), mat(0xf5f0e0));
    cheek.position.set(side * 0.09, 1.68, -0.10);
    cheek.scale.set(1.0, 0.8, 0.4);
    group.add(cheek);
  }

  // Dog ears poking through beanie
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(
      new THREE.ConeGeometry(0.04, 0.08, 6),
      mat(0xd4943a),
    );
    ear.position.set(side * 0.10, 1.90, 0);
    ear.rotation.z = side * 0.15;
    group.add(ear);
  }

  // Snout
  const snout = new THREE.Mesh(
    new THREE.BoxGeometry(0.08, 0.05, 0.07),
    mat(0xf0e0c0),
  );
  snout.position.set(0, 1.65, -0.16);
  group.add(snout);
  const dogNose = new THREE.Mesh(new THREE.SphereGeometry(0.018, 8, 8), mat(0x0a0a0a));
  dogNose.position.set(0, 1.67, -0.20);
  group.add(dogNose);

  // Dark eyes
  for (const side of [-0.055, 0.055]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.02, 8, 8), mat(0x1a0a00));
    eye.position.set(side, 1.74, -0.15);
    group.add(eye);
    const shine = new THREE.Mesh(new THREE.SphereGeometry(0.006, 4, 4), mat(0xffffff));
    shine.position.set(side - 0.007, 1.745, -0.165);
    group.add(shine);
  }

  // THE PINK BEANIE — the signature element
  const beanie = new THREE.Mesh(
    new THREE.SphereGeometry(0.20, SEG, SEG, 0, Math.PI * 2, 0, Math.PI * 0.55),
    mat(beanieColor),
  );
  beanie.position.set(0, 1.78, 0.01);
  group.add(beanie);
  // Beanie ribbed brim (rolled edge)
  const brim = new THREE.Mesh(
    new THREE.TorusGeometry(0.17, 0.02, 8, 16),
    mat(darken(beanieColor, 0.85)),
  );
  brim.position.set(0, 1.76, 0);
  brim.rotation.x = Math.PI / 2;
  group.add(brim);
  // Beanie pom-pom on top
  const pompom = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 8, 8),
    mat(lighten(beanieColor, 1.2)),
  );
  pompom.position.set(0, 1.98, 0.02);
  group.add(pompom);

  // Knitted texture lines on beanie (decorative ridges)
  for (let i = 0; i < 4; i++) {
    const ridge = new THREE.Mesh(
      new THREE.TorusGeometry(0.18 - i * 0.02, 0.004, 4, 16),
      mat(darken(beanieColor, 0.9)),
    );
    ridge.position.set(0, 1.80 + i * 0.04, 0.01);
    ridge.rotation.x = Math.PI / 2;
    group.add(ridge);
  }

  // Pink accent scarf
  const scarf = new THREE.Mesh(
    new THREE.TorusGeometry(0.12, 0.02, 6, 12),
    mat(beanieColor),
  );
  scarf.position.set(0, 1.48, -0.02);
  scarf.rotation.x = Math.PI / 2 + 0.15;
  group.add(scarf);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #23 — Floki: Viking warrior with horned helmet, Norse war paint,  */
/*        fur-lined vest, rune engravings. Named after Musk's Shiba.  */
/* ------------------------------------------------------------------ */

export function buildFloki(color: number): THREE.Group {
  const group = new THREE.Group();
  const furColor = 0x5a4a3a; // brown fur
  const skinColor = 0xd8c0a8;

  const body = buildHumanoid({
    bodyColor: furColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.25,
    armColor: furColor,
    legColor: 0x3a3a2a,
    bootColor: 0x4a3a2a,
    handColor: furColor,
    shoulderWidth: 0.30,
    muscular: true,
    eyeColor: 0x556644,
  });
  group.add(body);

  // Viking helmet — dark iron with orange horns
  const helmetBase = new THREE.Mesh(
    new THREE.SphereGeometry(0.17, SEG, SEG, 0, Math.PI * 2, 0, Math.PI * 0.6),
    mat(0x3a3a3a, { roughness: 0.4, metalness: 0.7 }),
  );
  helmetBase.position.set(0, 1.76, 0);
  group.add(helmetBase);

  // Helmet nose guard
  const noseGuard = new THREE.Mesh(
    new THREE.BoxGeometry(0.02, 0.10, 0.04),
    mat(0x3a3a3a, { roughness: 0.4, metalness: 0.7 }),
  );
  noseGuard.position.set(0, 1.68, -0.14);
  group.add(noseGuard);

  // ORANGE HORNS — the signature Floki element
  for (const side of [-1, 1]) {
    const horn = new THREE.Mesh(
      new THREE.ConeGeometry(0.025, 0.18, 8),
      mat(color, { roughness: 0.4, metalness: 0.5 }),
    );
    horn.position.set(side * 0.14, 1.82, 0.02);
    horn.rotation.z = side * 0.6;
    horn.rotation.x = -0.2;
    group.add(horn);
    // Horn rings
    for (let r = 0; r < 3; r++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.018 - r * 0.003, 0.003, 4, 8),
        mat(darken(color, 0.7)),
      );
      const t = (r + 1) / 4;
      ring.position.set(
        side * (0.14 + t * 0.06 * side),
        1.82 + t * 0.08,
        0.02 - t * 0.02,
      );
      ring.rotation.z = side * 0.6;
      group.add(ring);
    }
  }

  // War paint stripes under eyes
  for (const side of [-1, 1]) {
    const paint = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.01, 0.01),
      mat(0x1a1a1a),
    );
    paint.position.set(side * 0.04, 1.63, -0.13);
    group.add(paint);
    const paint2 = new THREE.Mesh(
      new THREE.BoxGeometry(0.035, 0.008, 0.01),
      mat(0x1a1a1a),
    );
    paint2.position.set(side * 0.04, 1.61, -0.13);
    group.add(paint2);
  }

  // Fur-lined vest collar
  const furCollar = new THREE.Mesh(
    new THREE.TorusGeometry(0.16, 0.03, 8, 12, Math.PI * 1.2),
    mat(0x7a6a5a),
  );
  furCollar.position.set(0, 1.44, -0.06);
  furCollar.rotation.x = -0.2;
  furCollar.rotation.y = Math.PI * 0.1;
  group.add(furCollar);

  // Leather belt with Norse buckle
  const beltLeather = new THREE.Mesh(
    new THREE.BoxGeometry(0.34, 0.05, 0.28),
    mat(0x4a3020),
  );
  beltLeather.position.set(0, 0.96, 0);
  group.add(beltLeather);
  const buckle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.025, 0.025, 0.006, 8),
    mat(0xccaa44, { roughness: 0.2, metalness: 0.8 }),
  );
  buckle.position.set(0, 0.96, -0.15);
  buckle.rotation.x = Math.PI / 2;
  group.add(buckle);

  // Rune engravings on chest (small geometric shapes)
  const runePositions = [
    [-0.06, 1.28], [0.0, 1.30], [0.06, 1.28],
  ];
  for (const [rx, ry] of runePositions) {
    const rune = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, 0.04, 0.005),
      matEmissive(color, 0.6),
    );
    rune.position.set(rx, ry, -0.20);
    rune.rotation.z = Math.random() * 0.5 - 0.25;
    group.add(rune);
  }

  // Fur arm wraps
  for (const side of [-1, 1]) {
    const wrap = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.05, 0.06, 8),
      mat(0x7a6a5a),
    );
    wrap.position.set(side * 0.28, 1.10, 0);
    group.add(wrap);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #24 — MOG: Pit Viper sunglasses tech-bro, chrome/reflective,     */
/*        sleek fitted gear, joycat patch, "mogging" confidence.      */
/* ------------------------------------------------------------------ */

export function buildMog(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = 0x1a1a1a; // sleek black
  const skinColor = 0xe0c8a8;
  const chromeColor = color; // 0x888888

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.15,
    armColor: suitColor,
    legColor: suitColor,
    bootColor: 0x111111,
    handColor: suitColor,
    eyeColor: 0x3a6688,
  });
  group.add(body);

  // Slicked-back dark hair
  const hair = new THREE.Mesh(
    new THREE.SphereGeometry(0.15, SEG, 10, 0, Math.PI * 2, 0, Math.PI * 0.48),
    mat(0x1a1a1a),
  );
  hair.position.set(0, 1.74, 0.02);
  group.add(hair);

  // PIT VIPER SUNGLASSES — the signature MOG element
  // Single wraparound lens
  const lensWrap = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.05, 0.04),
    mat(chromeColor, { roughness: 0.05, metalness: 0.95 }),
  );
  lensWrap.position.set(0, 1.70, -0.13);
  group.add(lensWrap);
  // Rainbow iridescent top strip on glasses
  const iridStrip = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.01, 0.04),
    matEmissive(0xff00ff, 0.4),
  );
  iridStrip.position.set(0, 1.725, -0.13);
  group.add(iridStrip);
  // Frame arms
  for (const side of [-1, 1]) {
    const arm = new THREE.Mesh(
      new THREE.BoxGeometry(0.005, 0.005, 0.12),
      mat(chromeColor, { roughness: 0.1, metalness: 0.9 }),
    );
    arm.position.set(side * 0.12, 1.71, -0.07);
    group.add(arm);
  }

  // Confident smirk
  const smirk = new THREE.Mesh(
    new THREE.TorusGeometry(0.022, 0.004, 4, 8, Math.PI * 0.6),
    mat(0xcc8888),
  );
  smirk.position.set(0.02, 1.55, -0.125);
  smirk.rotation.x = Math.PI;
  smirk.rotation.z = 0.1;
  group.add(smirk);

  // Joycat laughing cat patch on chest
  // Cat face circle
  const catPatch = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.03, 0.005, 10),
    mat(0xffcc00),
  );
  catPatch.position.set(-0.06, 1.28, -0.20);
  catPatch.rotation.x = Math.PI / 2;
  group.add(catPatch);
  // Cat ears on patch
  for (const side of [-1, 1]) {
    const catEar = new THREE.Mesh(
      new THREE.ConeGeometry(0.008, 0.015, 4),
      mat(0xffcc00),
    );
    catEar.position.set(-0.06 + side * 0.02, 1.31, -0.20);
    group.add(catEar);
  }

  // Sleek fitted tactical vest (minimal, clean lines)
  const vestLine = new THREE.Mesh(
    new THREE.BoxGeometry(0.005, 0.30, 0.005),
    mat(chromeColor, { roughness: 0.1, metalness: 0.9 }),
  );
  vestLine.position.set(0, 1.20, -0.19);
  group.add(vestLine);
  // Horizontal chrome accent
  const hLine = new THREE.Mesh(
    new THREE.BoxGeometry(0.20, 0.005, 0.005),
    mat(chromeColor, { roughness: 0.1, metalness: 0.9 }),
  );
  hLine.position.set(0, 1.12, -0.19);
  group.add(hLine);

  // Chrome bracelet on each wrist
  for (const side of [-1, 1]) {
    const bracelet = new THREE.Mesh(
      new THREE.TorusGeometry(0.035, 0.005, 6, 10),
      mat(chromeColor, { roughness: 0.05, metalness: 0.95 }),
    );
    bracelet.position.set(side * 0.28, 0.90, 0);
    bracelet.rotation.x = Math.PI / 2;
    group.add(bracelet);
  }

  // Clean white sneakers (over boots)
  for (const side of [-1, 1]) {
    const sneaker = new THREE.Mesh(
      new THREE.BoxGeometry(0.10, 0.04, 0.16),
      mat(0xffffff),
    );
    sneaker.position.set(side * 0.09, 0.04, -0.02);
    group.add(sneaker);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #25 — BRETT: Blue frog from Boy's Club comics. Base chain mascot, */
/*        chill gamer with headphones, indifferent expression.        */
/* ------------------------------------------------------------------ */

export function buildBrett(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = darken(color, 0.5);
  const skinColor = 0xc8b098;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.1,
    armColor: suitColor,
    legColor: 0x2a2a44,
    bootColor: 0x222222,
    handColor: color, // blue gloves
    showFace: false, // Brett frog face
  });
  group.add(body);

  // BRETT FROG HEAD — round blue head (different from Pepe: blue, calmer)
  const brettHead = new THREE.Mesh(new THREE.SphereGeometry(0.20, SEG, SEG), mat(color));
  brettHead.position.set(0, 1.72, 0);
  brettHead.scale.set(1.08, 0.95, 0.95);
  group.add(brettHead);

  // Half-lidded indifferent eyes (smaller than Pepe, more relaxed)
  for (const side of [-1, 1]) {
    const eyeWhite = new THREE.Mesh(new THREE.SphereGeometry(0.05, 10, 10), mat(0xffffff));
    eyeWhite.position.set(side * 0.09, 1.80, -0.10);
    eyeWhite.scale.set(1.0, 0.6, 0.5); // half-lidded
    group.add(eyeWhite);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.025, 8, 8), mat(0x0a0a0a));
    pupil.position.set(side * 0.09, 1.79, -0.14);
    group.add(pupil);
    // Heavy eyelid (covers top half of eye)
    const lid = new THREE.Mesh(
      new THREE.SphereGeometry(0.052, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.4),
      mat(color),
    );
    lid.position.set(side * 0.09, 1.82, -0.10);
    lid.scale.set(1.0, 0.5, 0.5);
    group.add(lid);
  }

  // Neutral flat mouth (not Pepe's wide mouth — Brett is chill)
  const mouth = new THREE.Mesh(
    new THREE.BoxGeometry(0.12, 0.015, 0.04),
    mat(darken(color, 0.5)),
  );
  mouth.position.set(0, 1.62, -0.17);
  group.add(mouth);

  // Headphones around neck — gamer accessory
  const headband = new THREE.Mesh(
    new THREE.TorusGeometry(0.14, 0.012, 6, 16, Math.PI),
    mat(0x333333),
  );
  headband.position.set(0, 1.48, -0.02);
  headband.rotation.x = 0.4;
  group.add(headband);
  // Ear cups hanging at sides
  for (const side of [-1, 1]) {
    const cup = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.03, 0.015, 8),
      mat(0x222222),
    );
    cup.position.set(side * 0.14, 1.44, 0.02);
    cup.rotation.z = Math.PI / 2;
    group.add(cup);
    const cushion = new THREE.Mesh(
      new THREE.CylinderGeometry(0.025, 0.025, 0.008, 8),
      mat(0x444444),
    );
    cushion.position.set(side * 0.145, 1.44, 0.02);
    cushion.rotation.z = Math.PI / 2;
    group.add(cushion);
  }

  // Base chain hexagon logo on chest
  const hex = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.03, 0.005, 6),
    mat(lighten(color, 1.4), { roughness: 0.3, metalness: 0.5 }),
  );
  hex.position.set(0, 1.24, -0.20);
  hex.rotation.x = Math.PI / 2;
  group.add(hex);

  // Lighter blue belly
  const belly = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 8, 8),
    mat(lighten(color, 1.5)),
  );
  belly.position.set(0, 1.12, -0.14);
  belly.scale.set(1, 1.1, 0.3);
  group.add(belly);

  // Gaming controller charm on belt
  const controller = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.025, 0.01),
    mat(0x444444),
  );
  controller.position.set(0.10, 0.94, -0.14);
  group.add(controller);
  // Dpad cross on controller
  const dpad = new THREE.Mesh(
    new THREE.BoxGeometry(0.015, 0.015, 0.005),
    mat(0x888888),
  );
  dpad.position.set(0.095, 0.94, -0.15);
  group.add(dpad);

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #26 — POPCAT: Orange tabby cat with wide-open mouth. Playful,    */
/*        cartoonish, based on the viral clicker game meme.           */
/* ------------------------------------------------------------------ */

export function buildPopcat(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = darken(color, 0.5);
  const skinColor = 0xd4b896;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.1,
    armColor: suitColor,
    legColor: darken(suitColor, 0.8),
    bootColor: darken(color, 0.4),
    handColor: color,
    showFace: false, // Cat face
  });
  group.add(body);

  // Cat head — round orange
  const catHead = new THREE.Mesh(new THREE.SphereGeometry(0.19, SEG, SEG), mat(color));
  catHead.position.set(0, 1.72, 0);
  catHead.scale.set(1.08, 1.0, 0.92);
  group.add(catHead);

  // Big round eyes (wide, surprised)
  for (const side of [-1, 1]) {
    const eyeWhite = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 10), mat(0xffffff));
    eyeWhite.position.set(side * 0.08, 1.78, -0.12);
    group.add(eyeWhite);
    const iris = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), mat(0x3a8833));
    iris.position.set(side * 0.08, 1.78, -0.16);
    group.add(iris);
    const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.018, 6, 6), mat(0x050505));
    pupil.position.set(side * 0.08, 1.78, -0.175);
    group.add(pupil);
    const shine = new THREE.Mesh(new THREE.SphereGeometry(0.008, 4, 4), mat(0xffffff));
    shine.position.set(side * 0.08 - 0.01, 1.79, -0.18);
    group.add(shine);
  }

  // Cat ears (pointed)
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(
      new THREE.ConeGeometry(0.045, 0.10, 6),
      mat(color),
    );
    ear.position.set(side * 0.11, 1.92, 0.02);
    ear.rotation.z = side * 0.15;
    group.add(ear);
    const innerEar = new THREE.Mesh(
      new THREE.ConeGeometry(0.025, 0.06, 6),
      mat(0xffaaaa),
    );
    innerEar.position.set(side * 0.11, 1.91, 0.0);
    innerEar.rotation.z = side * 0.15;
    group.add(innerEar);
  }

  // WIDE OPEN MOUTH — the signature POPCAT element
  // Upper jaw area
  const upperJaw = new THREE.Mesh(
    new THREE.SphereGeometry(0.10, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.5),
    mat(color),
  );
  upperJaw.position.set(0, 1.66, -0.10);
  upperJaw.scale.set(1.0, 0.6, 0.8);
  group.add(upperJaw);
  // Wide open mouth hole
  const mouthHole = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 10, 10),
    mat(0x1a0a0a),
  );
  mouthHole.position.set(0, 1.60, -0.14);
  mouthHole.scale.set(1.2, 1.0, 0.5);
  group.add(mouthHole);
  // Pink tongue inside
  const tongue = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 8, 8),
    mat(0xff6688),
  );
  tongue.position.set(0, 1.57, -0.13);
  tongue.scale.set(1.2, 0.5, 0.6);
  group.add(tongue);

  // Small nose
  const catNose = new THREE.Mesh(
    new THREE.ConeGeometry(0.012, 0.015, 4),
    mat(0xffaaaa),
  );
  catNose.position.set(0, 1.67, -0.18);
  catNose.rotation.x = Math.PI;
  group.add(catNose);

  // Whiskers
  for (const side of [-1, 1]) {
    for (let w = 0; w < 3; w++) {
      const whisker = new THREE.Mesh(
        new THREE.CylinderGeometry(0.002, 0.001, 0.10, 4),
        mat(0xcccccc),
      );
      whisker.position.set(side * 0.10, 1.64 + w * 0.03, -0.13);
      whisker.rotation.z = Math.PI / 2 + side * (0.1 + w * 0.1);
      group.add(whisker);
    }
  }

  // White chest patch
  const chestPatch = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 8, 8),
    mat(0xf5f0e0),
  );
  chestPatch.position.set(0, 1.14, -0.14);
  chestPatch.scale.set(0.8, 1.1, 0.3);
  group.add(chestPatch);

  // Tabby stripes on suit
  for (let s = 0; s < 3; s++) {
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.015, 0.01),
      mat(darken(color, 0.6)),
    );
    stripe.position.set(0, 1.30 - s * 0.06, -0.19);
    stripe.rotation.z = (s - 1) * 0.1;
    group.add(stripe);
  }

  // Tail
  for (let i = 0; i < 5; i++) {
    const t = i / 4;
    const tailSeg = new THREE.Mesh(
      new THREE.SphereGeometry(0.02 - t * 0.004, 6, 6),
      mat(i % 2 === 0 ? color : darken(color, 0.6)),
    );
    tailSeg.position.set(
      0,
      0.90 + Math.sin(t * Math.PI * 0.7) * 0.15,
      0.18 + t * 0.10,
    );
    group.add(tailSeg);
  }

  enableShadows(group);
  return group;
}

/* ------------------------------------------------------------------ */
/*  #27 — SPX6900: Bloomberg terminal trader. Green-on-black          */
/*        financial aesthetic, stock ticker accents, candlestick       */
/*        patterns, "6900" wall street degen energy.                  */
/* ------------------------------------------------------------------ */

export function buildSpx6900(color: number): THREE.Group {
  const group = new THREE.Group();
  const suitColor = 0x0a0a0a; // terminal black
  const termGreen = color; // 0x00CC66
  const skinColor = 0xd8c0a0;

  const body = buildHumanoid({
    bodyColor: suitColor,
    headColor: skinColor,
    skinColor,
    torsoScaleX: 1.15,
    armColor: 0x1a1a2a, // dark suit
    legColor: 0x1a1a2a,
    bootColor: 0x111111,
    handColor: skinColor,
    eyeColor: 0x3a6a3a,
  });
  group.add(body);

  // Slicked Wall Street hair
  addShortHair(group, 0x2a2a2a, 1.66, 0.14);

  // Loosened tie (trading floor chaos)
  const tie = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 0.20, 0.01),
    mat(termGreen),
  );
  tie.position.set(0, 1.26, -0.19);
  tie.rotation.z = 0.08; // slightly askew
  group.add(tie);
  // Tie knot
  const tieKnot = new THREE.Mesh(
    new THREE.SphereGeometry(0.018, 6, 6),
    mat(darken(termGreen, 0.7)),
  );
  tieKnot.position.set(0, 1.38, -0.19);
  group.add(tieKnot);

  // White shirt collar visible
  const collarL = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.035, 0.01),
    mat(0xeeeeee),
  );
  collarL.position.set(-0.05, 1.40, -0.17);
  collarL.rotation.z = 0.3;
  group.add(collarL);
  const collarR = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.035, 0.01),
    mat(0xeeeeee),
  );
  collarR.position.set(0.05, 1.40, -0.17);
  collarR.rotation.z = -0.3;
  group.add(collarR);

  // Rolled-up sleeves (forearm exposure) — lighter inner color
  for (const side of [-1, 1]) {
    const sleeveRoll = new THREE.Mesh(
      new THREE.CylinderGeometry(0.048, 0.046, 0.03, 8),
      mat(0x2a2a3a),
    );
    sleeveRoll.position.set(side * 0.28, 1.08, 0);
    group.add(sleeveRoll);
  }

  // Green terminal screen on chest (main display)
  const screen = new THREE.Mesh(
    new THREE.BoxGeometry(0.20, 0.12, 0.005),
    mat(0x0a0a0a),
  );
  screen.position.set(0, 1.18, -0.20);
  group.add(screen);
  // Screen border glow
  const screenGlow = new THREE.Mesh(
    new THREE.BoxGeometry(0.21, 0.13, 0.003),
    matEmissive(termGreen, 0.3),
  );
  screenGlow.position.set(0, 1.18, -0.201);
  group.add(screenGlow);

  // Candlestick chart on screen (green candles = bullish)
  const candleXs = [-0.06, -0.03, 0, 0.03, 0.06];
  const candleHs = [0.04, 0.06, 0.03, 0.07, 0.05];
  const candleYs = [1.16, 1.17, 1.15, 1.18, 1.17];
  for (let c = 0; c < 5; c++) {
    // Candle body
    const candle = new THREE.Mesh(
      new THREE.BoxGeometry(0.015, candleHs[c], 0.003),
      matEmissive(c === 2 ? 0xcc3333 : termGreen, 1.5), // one red candle
    );
    candle.position.set(candleXs[c], candleYs[c], -0.207);
    group.add(candle);
    // Wick
    const wick = new THREE.Mesh(
      new THREE.BoxGeometry(0.003, candleHs[c] + 0.02, 0.002),
      matEmissive(c === 2 ? 0xcc3333 : termGreen, 0.8),
    );
    wick.position.set(candleXs[c], candleYs[c], -0.208);
    group.add(wick);
  }

  // "6900" text approximation on screen bottom (block digits)
  const digitPositions = [-0.05, -0.02, 0.02, 0.05];
  for (const dx of digitPositions) {
    const digit = new THREE.Mesh(
      new THREE.BoxGeometry(0.018, 0.025, 0.002),
      matEmissive(termGreen, 2),
    );
    digit.position.set(dx, 1.11, -0.207);
    group.add(digit);
  }

  // Wrist-mounted mini screens (trader's tools)
  for (const side of [-1, 1]) {
    const miniScreen = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.025, 0.008),
      mat(0x0a0a0a),
    );
    miniScreen.position.set(side * 0.28, 0.94, -0.03);
    group.add(miniScreen);
    const miniGlow = new THREE.Mesh(
      new THREE.BoxGeometry(0.035, 0.02, 0.003),
      matEmissive(termGreen, 0.8),
    );
    miniGlow.position.set(side * 0.28, 0.94, -0.035);
    group.add(miniGlow);
  }

  // Aggressive expression — furrowed brows
  for (const side of [-1, 1]) {
    const furrow = new THREE.Mesh(
      new THREE.BoxGeometry(0.05, 0.012, 0.01),
      mat(darken(skinColor, 0.4)),
    );
    furrow.position.set(side * 0.04, 1.72, -0.12);
    furrow.rotation.z = side * 0.25;
    group.add(furrow);
  }

  enableShadows(group);
  return group;
}
